import {Col, Form, Input, InputNumber, Row, Select} from 'antd';
import React, {useCallback, useEffect} from 'react';

import {CommonErrors} from '../../../constants/Common';
import {yearSelect} from '../../../utils/FormUtils/yearSelect';
import {LCI_FIELD_HINTS} from '../constants/lciFieldHints';
import {
  PCF_COL_GUTTER_CLASS,
  PCF_COL_SPAN_HALF,
  PCF_COL_SPAN_THIRD,
  rowGutter
} from '../constants/pcfFormLayout';
import {
  ALLOCATION_BASES,
  INDIAN_STATES,
  MANUFACTURING_SETUPS,
  PRODUCTION_TIME_UNITS,
  REFERENCE_PERIODS
} from '../constants/pcfOptions';
import {ProductionDataKeys, ProductionDataLabels} from '../PCFKeyLabels';

import {FormItemLabel} from './FormItemLabel';
import {
  getSiteUnitsSumWarning,
  notifySiteUnitsSumOnSiteUnitsChange
} from './lciProductionUtils';
import {
  allocationFactorRule,
  positiveUnitsRule,
  siteUnitsSumRule
} from './lciValidation';
import PCFAddButton from './PCFAddButton';
import RemovableCard from './RemovableCard';

const BASE = 'production';

const blankSite = () => ({
  name: '',
  state: 'tamil_nadu',
  unitsProduced: '',
  productionTime: '',
  productionTimeUnit: 'hours',
  floorArea: '',
  facilityThroughput: ''
});

export const productionDataDefaults = {
  setup: 'single_site',
  period: 'annual',
  year: '2025',
  units: '',
  sites: [] as Array<ReturnType<typeof blankSite>>,
  allocationBasis: 'units_produced'
};

function SiteFields({
  name,
  onSiteUnitsProducedChange
}: {
  name: number;
  onSiteUnitsProducedChange: (value: number | string | null) => void;
}) {
  return (
    <Row gutter={rowGutter}>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, ProductionDataKeys.SITE_NAME]}
          label={ProductionDataLabels.SITE_NAME}
          rules={[
            {
              required: true,
              whitespace: true,
              message: CommonErrors.mandatory(ProductionDataLabels.SITE_NAME)
            }
          ]}
          validateFirst
          hasFeedback
        >
          <Input className="input" placeholder="e.g. Chennai Plant" />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, ProductionDataKeys.SITE_STATE]}
          label={ProductionDataLabels.SITE_STATE}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(ProductionDataLabels.SITE_STATE)
            }
          ]}
          validateFirst
          hasFeedback
        >
          <Select className="select" options={INDIAN_STATES} />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_THIRD}>
        <Form.Item
          name={[name, ProductionDataKeys.SITE_UNITS_PRODUCED]}
          required
          label={
            <FormItemLabel hint={LCI_FIELD_HINTS.siteUnits}>
              {ProductionDataLabels.SITE_UNITS_PRODUCED}
            </FormItemLabel>
          }
          rules={[positiveUnitsRule(ProductionDataLabels.SITE_UNITS_PRODUCED)]}
          validateFirst
          hasFeedback
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
            onChange={onSiteUnitsProducedChange}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_THIRD}>
        <Form.Item
          name={[name, ProductionDataKeys.SITE_PRODUCTION_TIME]}
          label={ProductionDataLabels.SITE_PRODUCTION_TIME}
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_THIRD}>
        <Form.Item
          name={[name, ProductionDataKeys.SITE_PRODUCTION_TIME_UNIT]}
          label={ProductionDataLabels.SITE_PRODUCTION_TIME_UNIT}
        >
          <Select className="select" options={PRODUCTION_TIME_UNITS} />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, ProductionDataKeys.SITE_FLOOR_AREA]}
          label={ProductionDataLabels.SITE_FLOOR_AREA}
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, ProductionDataKeys.SITE_FACILITY_THROUGHPUT]}
          required
          label={
            <FormItemLabel hint={LCI_FIELD_HINTS.facilityThroughput}>
              {ProductionDataLabels.SITE_FACILITY_THROUGHPUT}
            </FormItemLabel>
          }
          rules={[
            positiveUnitsRule(ProductionDataLabels.SITE_FACILITY_THROUGHPUT)
          ]}
          validateFirst
          hasFeedback
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
    </Row>
  );
}

const ProductionDataSection: React.FC = () => {
  const form = Form.useFormInstance();
  const production = Form.useWatch(BASE, form) ?? productionDataDefaults;
  const setup = production.setup ?? 'single_site';
  const showSites =
    setup === 'multiple_sites' || setup === 'contract_manufacturer';
  const sumMismatchMessage = getSiteUnitsSumWarning(production);

  useEffect(() => {
    if (!showSites) {
      return;
    }

    const sites = form.getFieldValue([BASE, ProductionDataKeys.SITES]) ?? [];

    if (!sites.length) {
      form.setFieldValue([BASE, ProductionDataKeys.SITES], [blankSite()]);
    }
  }, [showSites, setup, form]);

  const handleSiteUnitsProducedChange = useCallback(
    (siteIndex: number) => (value: number | string | null) => {
      const currentProduction =
        form.getFieldValue(BASE) ?? productionDataDefaults;
      const sites = [...(currentProduction.sites ?? [])];

      if (sites[siteIndex]) {
        sites[siteIndex] = {
          ...sites[siteIndex],
          unitsProduced: value ?? ''
        };
      }

      notifySiteUnitsSumOnSiteUnitsChange({
        ...currentProduction,
        sites
      });
    },
    [form]
  );

  return (
    <div className="lci-section">
      <h3 className="lci-section-title">Product Production Data</h3>

      <Form.Item
        name={BASE}
        rules={[siteUnitsSumRule(), allocationFactorRule()]}
        hidden
      >
        <span />
      </Form.Item>

      <Row gutter={rowGutter}>
        <Col className={PCF_COL_GUTTER_CLASS} span={24}>
          <Form.Item
            name={[BASE, ProductionDataKeys.MANUFACTURING_SETUP]}
            label={ProductionDataLabels.MANUFACTURING_SETUP}
            rules={[{required: true, message: 'Required'}]}
            validateFirst
            hasFeedback
          >
            <Select
              className="select"
              placeholder="Select"
              options={MANUFACTURING_SETUPS}
              style={{width: '100%'}}
            />
          </Form.Item>
        </Col>
        <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
          <Form.Item
            name={[BASE, ProductionDataKeys.REFERENCE_PERIOD]}
            label={ProductionDataLabels.REFERENCE_PERIOD}
            rules={[{required: true, message: 'Required'}]}
            validateFirst
            hasFeedback
          >
            <Select
              className="select"
              placeholder="Select"
              options={REFERENCE_PERIODS}
              style={{width: '100%'}}
            />
          </Form.Item>
        </Col>
        <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
          <Form.Item
            name={[BASE, ProductionDataKeys.REFERENCE_YEAR]}
            label={ProductionDataLabels.REFERENCE_YEAR}
            rules={[{required: true, message: 'Required'}]}
            validateFirst
            hasFeedback
          >
            <Select
              className="select"
              placeholder="Select"
              options={yearSelect()}
              style={{width: '100%'}}
            />
          </Form.Item>
        </Col>
        <Col className={PCF_COL_GUTTER_CLASS} span={24}>
          <Form.Item
            name={[BASE, ProductionDataKeys.TOTAL_UNITS_PRODUCED]}
            required
            label={
              <FormItemLabel hint={LCI_FIELD_HINTS.units}>
                {ProductionDataLabels.TOTAL_UNITS_PRODUCED}
              </FormItemLabel>
            }
            rules={[
              positiveUnitsRule(ProductionDataLabels.TOTAL_UNITS_PRODUCED)
            ]}
            validateFirst
            hasFeedback
          >
            <InputNumber
              controls={false}
              className="input"
              placeholder="e.g. 48000"
              style={{width: '100%'}}
              min={0}
            />
          </Form.Item>
        </Col>
      </Row>

      {showSites && (
        <Row gutter={rowGutter}>
          <Col className={PCF_COL_GUTTER_CLASS} span={24}>
            <Form.Item
              name={[BASE, ProductionDataKeys.ALLOCATION_BASIS]}
              required
              label={
                <FormItemLabel hint={LCI_FIELD_HINTS.allocationBasis}>
                  {ProductionDataLabels.ALLOCATION_BASIS}
                </FormItemLabel>
              }
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(
                    ProductionDataLabels.ALLOCATION_BASIS
                  )
                }
              ]}
              validateFirst
              hasFeedback
            >
              <Select className="select" options={ALLOCATION_BASES} />
            </Form.Item>
          </Col>
        </Row>
      )}

      {showSites && (
        <Form.List name={[BASE, ProductionDataKeys.SITES]}>
          {(fields, {add, remove}, {errors: listErrors}) => (
            <>
              {listErrors?.length > 0 && (
                <div className="field-error">{listErrors[0]}</div>
              )}
              <div className="lci-section-header">
                <h4 className="subsection-title">
                  {ProductionDataLabels.SITES}
                </h4>
                <PCFAddButton
                  onClick={() => {
                    add(blankSite());
                  }}
                >
                  Add Site
                </PCFAddButton>
              </div>
              <div className="lci-cards-list">
                {fields.map(({key, name}, idx) => (
                  <RemovableCard
                    key={key}
                    title={`Site #${idx + 1}`}
                    onRemove={() => {
                      remove(name);
                    }}
                    showRemove={fields.length > 1}
                  >
                    <SiteFields
                      name={name}
                      onSiteUnitsProducedChange={handleSiteUnitsProducedChange(
                        name
                      )}
                    />
                  </RemovableCard>
                ))}
              </div>
            </>
          )}
        </Form.List>
      )}

      {sumMismatchMessage && (
        <div
          className="form-error-summary production-units-sum-error"
          role="alert"
          aria-live="assertive"
        >
          {sumMismatchMessage}
        </div>
      )}
    </div>
  );
};

export default ProductionDataSection;
